import React, { useState, useEffect, useMemo, useCallback } from "react";
import CTabs, { CTabPanel } from "../../components/CTabs";
import CButton from "../../components/CButton";
import CFormTextField from "../../components/HookFormComponents/CFormTextField";
import CFormProvider from "../../components/HookFormComponents/CFormProvider";
import { useForm, useFieldArray } from "react-hook-form";
import { CFormSelect } from "../../components/HookFormComponents/CFormSelect";
import CFormDatePicker from "../../components/HookFormComponents/CFormDatePicker";
import CDialog from "../../components/CDialog";
import FormHistoryCompareDialog from "../../components/FormHistoryCompareDialog";
import CommentDialog from "../../components/CommentDialog";
import { GridCloseIcon } from "@mui/x-data-grid";
import CDataGrid from "../../components/CDataGrid";
import CSelect from "../../components/CSelect";
import CDatePicker from "../../components/CDatePicker";
import CTextField from "../../components/CTextField";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import TrainingTypeCategoryMatrixDialog from "../../components/TrainingTypeCategoryMatrixDialog";
import { getUserDetails } from "../../slices/userSlice";
import { useDispatch, useSelector } from "react-redux";

import {
  getLocalData,
  isNoDepartment,
  ROWS_PER_PAGE_OPTIONS,
} from "../../constant/utils/commonUtils";
import { isCompanyPrivileged } from "../../utils/roleUtils";
import {
  currentDate,
  getDate,
  getEndOfDayFromISOString,
} from "../../constant/utils/datetimeUtils";
import { toast } from "react-toastify";
import { messages } from "../../constant/alertMessages";
import {
  addTrainingForm,
  fetchEmployeeForm,
  fetchEmployeeFormByYearId,
  fetchTrainingProgrammes,
  resetTrainingForm,
  updateTrainingForm,
  saveTrainingFormWithHistory,
} from "../../slices/trainingFormSlice";
import { ReactComponent as DeleteIcon } from "../../assets/images/trash-icon.svg";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import moment from "moment";
import { fetchComments } from "../../slices/commentSlice";
import { fetchConfiguration } from "../../slices/configSlice";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { checkApprovalRequired } from "../../slices/trainingFormReviewSlice";
import {
  statusType,
  trainingFormStatus,
  NQF_LEVEL_OPTIONS,
  NQF_ALIGNED_OPTIONS,
  statuses,
  trainingFormStatusLabels,
  roleType,
  COST_PER_LEARNER_LIMIT,
  FEATURE_FLAGS,
  PROGRAM_DURATION_OPTIONS,
  DEFAULT_TRAINING_ALLOCATION,
  UNEMPLOYED_MAX_TRAININGS_PER_SKILL,
} from "../../constant/constants";
import { TRAINING_CATALOGUE_PLACEHOLDERS } from "../../constant/placeholders";
import { fetchSetaTemplateByCompanyId } from "../../slices/setaTemplateSlice";
import { isUnemployedEmploymentType } from "../../constant/utils/employmentTypeUtils";
import { fetchOrganogramByDepartmentId } from "../../slices/organogramSlice";
import { fetchYearId } from "../../slices/yearSlice";
import { fromWithCompany, supabase } from "../../config/supabaseClient";
import { Tooltip, IconButton, useMediaQuery, useTheme } from "@mui/material";

/**
 * Unemployed: one skill row in UI; API may return multiple skill_requirements.
 * Collapse rowSelectionModel to one entry; keep only the last N trainings (replacement).
 */
function collapseSingleTrainingRowSelectionModel(rows, maxPerSkill) {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const first = rows[0];
  const ids = first.selectedTrainingIds || [];
  const infos = first.selectedTrainingsInfo || [];
  return [
    {
      ...first,
      selectedTrainingIds: ids.slice(-maxPerSkill),
      selectedTrainingsInfo: infos.slice(-maxPerSkill),
    },
  ];
}

const EmployeeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlEmpId = searchParams.get("emp_id") || null;
  const urlSdfOnBehalf = searchParams.get("sdf_on_behalf") === "1";
  const urlEmploymentType = searchParams.get("employment_type") || null;

  const [activeTab, setActiveTab] = useState("Training Needs Forms");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [dialogState, setDialogState] = useState({
    show: false,
    id: null,
    type: "",
  });
  const [approvalDialogState, setApprovalDialogState] = useState({
    show: false,
    id: null,
    formStatus: null,
  });
  const [formStatus, setFormStatus] = useState({
    status: null,
    to_id: null,
    dept_head_id: null,
    assignee_role: null,
  });
  // { skill: "", selectedTrainingIds: [], selectedTrainingsInfo: [] }
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [selectedSkillIndex, setSelectedSkillIndex] = useState({
    index: null,
    skill: "",
  });

  const [completionDate, setCompletionDate] = useState(null);
  const [numberOfSkills, setNumberOfSkills] = useState(3); // Default number of skills
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState(false);
  const [historyViewDialog, setHistoryViewDialog] = useState({
    open: false,
    row: null,
  });
  const [formValidationAttempted, setFormValidationAttempted] = useState(false);
  const [formHasChanges, setFormHasChanges] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [initialRowSelectionModel, setInitialRowSelectionModel] = useState([]);

  const [requestTrainingDialogOpen, setRequestTrainingDialogOpen] =
    useState(false);

  const [courseDetailsDialog, setCourseDetailsDialog] = useState({
    open: false,
    description: "",
    title: "",
  });

  const [showCategoryMatrixDialog, setShowCategoryMatrixDialog] =
    useState(false);

  const [isWideScreen, setIsWideScreen] = useState(window.innerWidth > 1100);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();
  const isHistoryCompact = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const handleResize = () => {
      setIsWideScreen(window.innerWidth > 1100);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Validation schema for request training form
  const requestTrainingValidationSchema = Yup.object().shape({
    trainingName: Yup.string()
      .required("Training name is required")
      .min(3, "Training name must be at least 3 characters"),
    // trainingType: Yup.string().required("Training type is required"),
    programDuration: Yup.string().required("Program duration is required"),
    targetedSkills: Yup.string().required("Motivation is required"),
    // trainingCostPerHead: Yup
    //   .number()
    //   .typeError("Cost per learner must be a number")
    //   .required("Cost per learner is required")
    //   .min(0, "Cost per learner cannot be negative")
    //   .max(COST_PER_LEARNER_LIMIT, messages.COST_PER_LEARNER_LIMIT_MESSAGE(COST_PER_LEARNER_LIMIT))
    //   .test(
    //     "is-decimal",
    //     "Cost per learner can have up to 2 decimal places",
    //     (value) => !value || /^\d+(\.\d{1,2})?$/.test(value)
    //   ),
    // trainingProviderName: Yup
    //   .string()
    //   .required("Training provider name is required"),
    // trainingDescription: Yup
    //   .string()
    //   .required("Training description is required")
    //   .min(10, "Description must be at least 10 characters"),
    // nqf_aligned: Yup.string().required("NQF Aligned is required"),
    // nqf_level: Yup.string().when("nqf_aligned", {
    //   is: (val) => val === "Yes",
    //   then: (schema) => schema.required("NQF Level is required when NQF Aligned is Yes"),
    //   otherwise: (schema) => schema.optional(),
    // }),
  });

  const requestTrainingForm = useForm({
    defaultValues: {
      trainingName: "",
      trainingType: "",
      programDuration: "",
      targetedSkills: "",
      trainingCostPerHead: "",
      trainingProviderName: "",
      nqf_aligned: "",
      nqf_level: "",
    },
    resolver: yupResolver(requestTrainingValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const nqfAlignedValue = requestTrainingForm.watch("nqf_aligned");

  const handleRequestTrainingAdd = async (data) => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      const localData = getLocalData();

      if (!localData?.id) {
        toast.error("User ID is required");
        setIsSubmitting(false);
        return;
      }

      // Prepare training request data
      const trainingRequestData = {
        company_id: localData.company_id,
        user_id: localData.id,
        name: data.trainingName,
        program_duration: data.programDuration, // Direct value, not JSON
        motivation: data.targetedSkills, // Use motivation column
        status: "requested", // New status value instead of "active"
        created_by: localData.first_name || localData.name || "",
        updated_by: localData.first_name || localData.name || "",
      };

      // Insert the training request
      const response = await supabase
        .from("training_requests")
        .insert(trainingRequestData)
        .select();

      if (response.error) {
        throw response.error;
      }

      setRequestTrainingDialogOpen(false);
      toast.success("Training request submitted successfully!");

      // Reset form after successful submission
      requestTrainingForm.reset();
      setIsSubmitting(false);
    } catch (error) {
      console.error("Error saving training request:", error);
      toast.error(error.message || "Error saving training request");
      setIsSubmitting(false);
    }
  };

  const handleRequestTrainingCancel = () => {
    setRequestTrainingDialogOpen(false);
    // Reset form and clear all errors
    requestTrainingForm.reset();
    requestTrainingForm.clearErrors();
  };

  const handleRequestTrainingOpen = () => {
    setRequestTrainingDialogOpen(true);
    // Reset form when opening to ensure clean state
    requestTrainingForm.reset();
    requestTrainingForm.clearErrors();
  };

  const handleDelete = () => {
    setDialogState({ id: null, show: false });
  };
  const tabs = [
    { label: "Training Needs Forms", value: "Training Needs Forms" },
    { label: "History", value: "Comments" },
  ];

  const [deleteDialogState, setDeleteDialogState] = useState({
    show: false,
    data: null,
  });

  const [userDetails, setUserDetails] = useState([]);
  const localData = JSON.parse(localStorage.getItem("sdfUser"));

  const dispatch = useDispatch();

  const {
    courses,
    comments,
    configData,
    selectedFormId,
    to_id,
    setaTemplate,
    loadedForm,
    employmentTypes,
  } = useSelector((state) => ({
    courses: state.trainingForm.courses,
    comments: state.comment.comments,
    configData: state.config.configData,
    selectedFormId: state.trainingForm.selectedFormId,
    to_id: state.trainingForm.to_id,
    setaTemplate: state.setaTemplate.setaTemplate,
    loadedForm: state.trainingForm.trainingForm,
    employmentTypes: state.setaTemplate.employmentTypes || [],
  }));
  const formEmployeeEmploymentType =
    loadedForm?.company_user?.employment_type ??
    (urlEmpId
      ? (userDetails?.employment_type ?? urlEmploymentType)
      : undefined) ??
    urlEmploymentType ??
    getLocalData()?.employment_type;
  // Unemployed (SETA isUnemployedType): one skill, one training
  const isUnemployed = Boolean(
    employmentTypes?.length &&
    isUnemployedEmploymentType(employmentTypes, formEmployeeEmploymentType),
  );
  const isSdfOnBehalfFlow = urlSdfOnBehalf && urlEmpId;

  // Validation Schema
  const TrainingFormSchema = Yup.object().shape({
    shortTermGoal: Yup.string(),
    longTermGoal: Yup.string(),
    AnyNotes: Yup.string(),
    skillsDetail: Yup.array()
      .of(
        Yup.object().shape({
          skillsRequired: Yup.string(),
          ...(!configData?.select_training && {
            priorityLevel: Yup.string().when("skillsRequired", {
              is: (val) => !!val?.trim(),
              then: (schema) => schema.required("Priority Level is required"),
              otherwise: (schema) => schema.notRequired().nullable(),
            }),
          }),
          programmeDuration: Yup.string().when("skillsRequired", {
            is: (val) => !!val?.trim(),
            then: (schema) => schema.required("Course Type is required"),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
          skillsRequirementMotivation: Yup.string().when("skillsRequired", {
            is: (val) => !!val?.trim(),
            then: (schema) =>
              schema
                .required("Skills Requirement Motivation is required")
                .min(
                  20,
                  "Skills Requirement Motivation must be at least 20 characters",
                ),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
          ...(!configData?.select_training &&
            FEATURE_FLAGS.SHOW_COMPLETION_DATE && {
              completionDate: Yup.date().when("skillsRequired", {
                is: (val) => !!val?.trim(),
                then: (schema) =>
                  schema
                    .required("Completion Date is required")
                    .min(
                      moment().startOf("day").toDate(),
                      "Completion date cannot be in the past",
                    ),
                otherwise: (schema) => schema.nullable(),
              }),
            }),
        }),
      )
      .test(
        "at-least-one-complete",
        "At least one skill detail must be complete",
        function (value) {
          if (!value || !Array.isArray(value) || value.length === 0) {
            return true;
          }

          const errors = [];
          let atLeastOneSkillEntryHasAnyData = false;

          for (let i = 0; i < value.length; i++) {
            const skill = value[i];

            const hasSkillsRequired = !!skill.skillsRequired?.trim();
            const hasPriorityLevel = !!skill.priorityLevel?.trim();
            const hasProgramDuration = !!skill.programmeDuration?.trim();
            const hasSkillMotivation =
              !!skill.skillsRequirementMotivation?.trim();
            const hasCompletionDate =
              !configData?.select_training && FEATURE_FLAGS.SHOW_COMPLETION_DATE
                ? !!skill.completionDate
                : false;

            // Determine if the current skill entry has ANY field filled (making it a partial entry candidate)
            const isCurrentSkillEntryPopulated =
              hasSkillsRequired ||
              hasPriorityLevel ||
              hasProgramDuration ||
              hasCompletionDate ||
              hasSkillMotivation;

            if (!isCurrentSkillEntryPopulated) {
              // Skip validation for completely empty rows
              continue;
            }

            // Mark that we found at least one populated skill entry
            atLeastOneSkillEntryHasAnyData = true;

            // if(hasSkillMotivation){
            //   if(!hasSkillsRequired){
            //     errors.push(this.createError({
            //       path: `skillsDetail.${i}.skillsRequired`,
            //       message: "Skills Required is mandatory if any other skill detail is provided.",
            //     }));
            //   }
            //   if(!hasTrainingType){
            //     errors.push(this.createError({
            //       path: `skillsDetail.${i}.trainingType`,
            //       message: "Training Type is required for this entry.",
            //     }));
            //   }
            //   if(!hasPriorityLevel){
            //     errors.push(this.createError({
            //       path: `skillsDetail.${i}.priorityLevel`,
            //       message: "Priority Level is required for this entry.",
            //     }));
            //   }
            //   if(!hasCompletionDate && !configData?.select_training){
            //     errors.push(this.createError({
            //       path: `skillsDetail.${i}.completionDate`,
            //       message: "Completion Date is required for this entry.",
            //     }));
            //   }
            // }else
            if (isCurrentSkillEntryPopulated) {
              if (!hasSkillsRequired) {
                errors.push(
                  this.createError({
                    path: `skillsDetail.${i}.skillsRequired`,
                    message:
                      "Skills Required is mandatory if any other skill detail is provided.",
                  }),
                );
              }
              if (!hasProgramDuration) {
                errors.push(
                  this.createError({
                    path: `skillsDetail.${i}.programDuration`,
                    message: "Course Type is required for this entry.",
                  }),
                );
              }
              if (!hasPriorityLevel) {
                errors.push(
                  this.createError({
                    path: `skillsDetail.${i}.priorityLevel`,
                    message: "Priority Level is required for this entry.",
                  }),
                );
              }
              if (!hasSkillMotivation) {
                errors.push(
                  this.createError({
                    path: `skillsDetail.${i}.skillsRequirementMotivation`,
                    message:
                      "Skills Requirement Motivation is required for this entry.",
                  }),
                );
              }
              if (
                !hasCompletionDate &&
                !configData?.select_training &&
                FEATURE_FLAGS.SHOW_COMPLETION_DATE
              ) {
                errors.push(
                  this.createError({
                    path: `skillsDetail.${i}.completionDate`,
                    message: "Completion Date is required for this entry.",
                  }),
                );
              }
            }

            // Rule 1: If skillsRequired is NOT filled, but other fields ARE filled, skillsRequired is mandatory.
            // if (!hasSkillsRequired) {
            //   errors.push(
            //     this.createError({
            //       path: `skillsDetail.${i}.skillsRequired`,
            //       message: "Skills Required is mandatory if any other skill detail is provided.",
            //     })
            //   );

            //     if (!hasTrainingType ) {
            //        errors.push(this.createError({ path: `skillsDetail.${i}.trainingType`, message: "Training Type is required for this entry." }));
            //     }
            //     if (!hasPriorityLevel ) {
            //         errors.push(this.createError({ path: `skillsDetail.${i}.priorityLevel`, message: "Priority Level is required for this entry." }));
            //     }
            //     // if (!hasCompletionDate && (hasSkillsRequired || hasTrainingType || hasPriorityLevel || hasSkillMotivation)) {
            //     //     errors.push(this.createError({ path: `skillsDetail.${i}.completionDate`, message: "Completion Date is required for this entry." }));
            //     // }

            // } else {
            //   // Rule 2: If skillsRequired IS filled, ensure other conditionally required fields are also filled.

            //     if (!hasTrainingType) {
            //       errors.push(
            //         this.createError({
            //           path: `skillsDetail.${i}.trainingType`,
            //           message: "Training Type is required for this entry.",
            //         })
            //       );
            //     }
            //     if (!hasPriorityLevel) {
            //       errors.push(
            //         this.createError({
            //           path: `skillsDetail.${i}.priorityLevel`,
            //           message: "Priority Level is required for this entry.",
            //         })
            //       );
            //     }
            //     if (!hasCompletionDate) {
            //       errors.push(
            //         this.createError({
            //           path: `skillsDetail.${i}.completionDate`,
            //           message: "Completion Date is required for this entry.",
            //         })
            //       );

            //   }
            // }
          }

          // If any errors were collected, throw them.
          if (errors.length > 0) {
            throw new Yup.ValidationError(errors);
          }

          // Check if at least one skill entry has any data
          if (!atLeastOneSkillEntryHasAnyData) {
            throw new Yup.ValidationError([
              this.createError({
                path: `skillsDetail.0.skillsRequired`,
                message: "At least one skill detail must be provided.",
              }),
            ]);
          }

          return true;
        },
      ),
  });

  const methods = useForm({
    defaultValues: {
      shortTermGoal: "",
      longTermGoal: "",
      AnyNotes: "",
      skillsDetail: Array(numberOfSkills)
        .fill()
        .map(() => ({
          skillsRequired: "",
          priorityLevel: "",
          programmeDuration: "",
          skillsRequirementMotivation: "",
          ...(!configData?.select_training &&
            FEATURE_FLAGS.SHOW_COMPLETION_DATE && {
              completionDate: null,
            }),
          selectedTrainings: [],
        })),
    },
    resolver: yupResolver(TrainingFormSchema),
    mode: "onChange",
    reValidateMode: "onSubmit",
  });

  const { fields } = useFieldArray({
    control: methods.control,
    name: "skillsDetail",
  });
  useEffect(() => {
    // Reset all component state when id changes
    setActiveTab("Training Needs Forms");
    setSearchQuery("");
    setRowSelectionModel([]);
    setInitialRowSelectionModel([]);
    setSelectedSkillIndex({ index: null, skill: null });
    setCompletionDate(null);
    setDialogState({ id: null, show: false });
    setApprovalDialogState({ id: null, show: false, formStatus: null });
    setComment("");
    setRequestTrainingDialogOpen(false);
    setFormStatus({
      status: null,
      to_id: null,
      dept_head_id: null,
      assignee_role: null,
    });

    // Reset form to default values
    const defaultFormData = {
      shortTermGoal: "",
      longTermGoal: "",
      AnyNotes: "",
      skillsDetail: Array(numberOfSkills)
        .fill()
        .map(() => ({
          skillsRequired: "",
          priorityLevel: "",
          programmeDuration: "",
          skillsRequirementMotivation: "",
          ...(configData?.select_training &&
            FEATURE_FLAGS.SHOW_COMPLETION_DATE && {
              completionDate: null,
            }),
          selectedTrainings: [],
        })),
    };
    methods.reset(defaultFormData);
    setInitialFormData(JSON.parse(JSON.stringify(defaultFormData)));
    setFormHasChanges(false);

    // Reset request training form
    requestTrainingForm.reset();
    dispatch(resetTrainingForm());
  }, [id]);

  // Add useEffect to update form when numberOfSkills changes
  useEffect(() => {
    const currentSkills = methods.getValues("skillsDetail");
    const newSkills = Array(numberOfSkills)
      .fill()
      .map((_, index) => ({
        skillsRequired: currentSkills[index]?.skillsRequired || "",
        priorityLevel: currentSkills[index]?.priorityLevel || "",
        programmeDuration: currentSkills[index]?.programmeDuration || "",
        skillsRequirementMotivation:
          currentSkills[index]?.skillsRequirementMotivation || "",
        ...(configData?.select_training &&
          FEATURE_FLAGS.SHOW_COMPLETION_DATE && {
            completionDate: currentSkills[index]?.completionDate || null,
          }),
        selectedTrainings: currentSkills[index]?.selectedTrainings || [],
      }));
    const updatedFormData = {
      ...methods.getValues(),
      skillsDetail: newSkills,
    };
    methods.reset(updatedFormData);
    setInitialFormData(JSON.parse(JSON.stringify(updatedFormData)));
    setInitialRowSelectionModel(
      JSON.parse(JSON.stringify(rowSelectionModel)),
    );
    setFormHasChanges(false);
  }, [numberOfSkills]);

  // Unemployed: 1 skill row; otherwise 3 skill rows
  useEffect(() => {
    if (isUnemployed && numberOfSkills !== 1) {
      setNumberOfSkills(1);
    } else if (!isUnemployed && numberOfSkills !== 3) {
      setNumberOfSkills(3);
    }
  }, [isUnemployed, numberOfSkills]);

  const selectedTrainingsTableRows = useMemo(() => {
    if (!rowSelectionModel?.length) return [];
    if (isUnemployed) {
      const infos = rowSelectionModel[0]?.selectedTrainingsInfo || [];
      return infos.slice(-UNEMPLOYED_MAX_TRAININGS_PER_SKILL);
    }
    return rowSelectionModel.flatMap((row) => row.selectedTrainingsInfo || []);
  }, [rowSelectionModel, isUnemployed]);

  useEffect(() => {
    if (!isUnemployed) return;
    setRowSelectionModel((prev) => {
      if (!Array.isArray(prev) || prev.length <= 1) return prev;
      return collapseSingleTrainingRowSelectionModel(
        prev,
        UNEMPLOYED_MAX_TRAININGS_PER_SKILL,
      );
    });
  }, [isUnemployed]);

  const trainingOptions = [
    { id: 1, value: "Training 1" },
    { id: 2, value: "Training 2" },
  ];

  const priorityOptions = [
    { id: "high", value: "High" },
    { id: "medium", value: "Medium" },
    { id: "low", value: "Low" },
  ];

  const [collectionStatus, setCollectionStatus] = useState({
    accessible: false,
    message: null,
  });

  useEffect(() => {
    if (setaTemplate.length) return;
    const companyId =
      getLocalData("sdfUser")?.company_id ?? getLocalData()?.company_id;
    if (!companyId) return;
    dispatch(fetchSetaTemplateByCompanyId(companyId));
  }, [id, setaTemplate]);

  // Reset request training form when dialog closes
  useEffect(() => {
    if (!requestTrainingDialogOpen) {
      requestTrainingForm.reset();
      requestTrainingForm.clearErrors();
    }
  }, [requestTrainingDialogOpen]);

  useEffect(() => {
    const data = getLocalData();
    const companyId = data?.company_id;
    if (companyId) {
      dispatch(fetchYearId()).then((yearRes) => {
        if (yearRes.meta.requestStatus === "fulfilled" && yearRes.payload) {
          dispatch(
            fetchConfiguration({ yearId: yearRes.payload, companyId }),
          );
        }
      });
    }
  }, [dispatch]);

  useEffect(() => {
    let data = getLocalData();
    const isCollectionConfigured =
      configData?.training_need_workflow &&
      configData?.training_need_collection_start_date;
    const canLoadFormTab =
      activeTab === "Training Needs Forms" && (id || isCollectionConfigured);

    if (!setaTemplate.length) return;
    if (canLoadFormTab) {
      if (id) {
        dispatch(fetchEmployeeForm(id)).then((res) => {
          if (res.meta.requestStatus === "fulfilled") {
            const {
              dept_head_id,
              to_id,
              form_status,
              formattedData,
              selectedRows,
              emp_id,
              assignee_role,
            } = res.payload;
            let updatedSelectedRow = [...formattedData.skillsDetail];
            if (isUnemployed) {
              updatedSelectedRow = updatedSelectedRow.slice(0, 1);
              if (updatedSelectedRow[0]) {
                const sel = updatedSelectedRow[0].selectedTrainings;
                updatedSelectedRow[0] = {
                  ...updatedSelectedRow[0],
                  selectedTrainings: Array.isArray(sel)
                    ? sel.slice(-UNEMPLOYED_MAX_TRAININGS_PER_SKILL)
                    : [],
                };
              }
            } else if (updatedSelectedRow.length < numberOfSkills) {
              const diff = numberOfSkills - updatedSelectedRow.length;
              for (let i = 0; i < diff; i++) {
                updatedSelectedRow.push({
                  skillsRequired: "",
                  priorityLevel: "",
                  skillsRequirementMotivation: "",
                  ...(FEATURE_FLAGS.SHOW_COMPLETION_DATE && {
                    completionDate: null,
                  }),
                  selectedTrainings: [],
                });
              }
            }

            const updatedFormattedData = {
              ...formattedData,
              skillsDetail: updatedSelectedRow,
            };
            methods.reset(updatedFormattedData);
            setInitialFormData(
              JSON.parse(JSON.stringify(updatedFormattedData)),
            );
            setFormHasChanges(false);
            const normalizedSelectedRows =
              isUnemployed && selectedRows?.length
                ? collapseSingleTrainingRowSelectionModel(
                    selectedRows,
                    UNEMPLOYED_MAX_TRAININGS_PER_SKILL,
                  )
                : selectedRows;
            setRowSelectionModel(normalizedSelectedRows);
            setInitialRowSelectionModel(
              JSON.parse(JSON.stringify(normalizedSelectedRows || [])),
            );

            dispatch(getUserDetails(emp_id)).then(async (res) => {
              if (res.meta.requestStatus === "fulfilled") {
                let designation = "";
                setaTemplate.forEach((item) => {
                  if (item?.config?.isDesignation) {
                    designation = item?.fieldName;
                  }
                });
                const isUnemployedEmp =
                  employmentTypes?.length &&
                  isUnemployedEmploymentType(
                    employmentTypes,
                    res.payload?.employment_type,
                  );
                let department = { payload: null };
                if (
                  res.payload?.department_id &&
                  !isNoDepartment(res.payload.department_id) &&
                  !isUnemployedEmp
                ) {
                  department = await dispatch(
                    fetchOrganogramByDepartmentId(res.payload.department_id),
                  );
                }

                // Fetch department head name
                let departmentHeadName = "-";
                if (department.payload?.dept_head_id) {
                  try {
                    const { data: departmentHeadData, error } =
                      await fromWithCompany("company_user")
                        .select("first_name, last_name, data")
                        .eq("emp_id", department.payload.dept_head_id)
                        .neq("status", statuses.ARCHIVED)
                        .single();

                    if (!error && departmentHeadData) {
                      departmentHeadName =
                        departmentHeadData.first_name +
                          " " +
                          departmentHeadData.last_name ||
                        departmentHeadData.data?.Name ||
                        departmentHeadData.data?.name ||
                        "No Name";
                    }
                  } catch (error) {
                    console.error("Error fetching manager data:", error);
                  }
                }

                setUserDetails({
                  ...res.payload,
                  designation: res.payload.data[designation],
                  department: isUnemployedEmp
                    ? ""
                    : (department.payload?.dept_name ?? ""),
                  department_head_name: departmentHeadName,
                });
              }
            });
            setFormStatus({
              status: form_status,
              to_id: to_id,
              dept_head_id: dept_head_id,
              assignee_role: assignee_role,
            });
          } else if (res.meta.requestStatus === "rejected") {
            toast.error(res.payload || "Failed to load training form.");
          }
        });
      } else {
        const isSdfOnBehalfFlow =
          urlEmpId && isCompanyPrivileged(data?.role);
        const empIdToFetch = isSdfOnBehalfFlow ? urlEmpId : data.id;
        dispatch(fetchYearId()).then((res) => {
          if (res.meta.requestStatus === "fulfilled") {
            let yearId = res.payload;
            dispatch(
              fetchEmployeeFormByYearId({
                emp_id: empIdToFetch,
                year_id: yearId,
              }),
            ).then(async (res) => {
              if (res.meta.requestStatus === "fulfilled") {
                if (res.payload) {
                  const {
                    dept_head_id,
                    to_id,
                    form_status,
                    formattedData,
                    selectedRows,
                    assignee_role,
                  } = res.payload;
                  methods.reset(formattedData);
                  setInitialFormData(JSON.parse(JSON.stringify(formattedData)));
                  setFormHasChanges(false);
                  setFormStatus({
                    status: form_status,
                    to_id: to_id,
                    dept_head_id: dept_head_id,
                    assignee_role: assignee_role,
                  });
                  setRowSelectionModel(selectedRows);
                  setInitialRowSelectionModel(
                    JSON.parse(JSON.stringify(selectedRows || [])),
                  );
                } else if (isSdfOnBehalfFlow) {
                  const skillCount = isUnemployed ? 1 : numberOfSkills;
                  const emptyFormData = {
                    shortTermGoal: "",
                    longTermGoal: "",
                    AnyNotes: "",
                    skillsDetail: Array(skillCount)
                      .fill()
                      .map(() => ({
                        skillsRequired: "",
                        priorityLevel: "",
                        programmeDuration: "",
                        skillsRequirementMotivation: "",
                        ...(!configData?.select_training &&
                          FEATURE_FLAGS.SHOW_COMPLETION_DATE && {
                            completionDate: null,
                          }),
                        selectedTrainings: [],
                      })),
                  };
                  methods.reset(emptyFormData);
                  setInitialFormData(JSON.parse(JSON.stringify(emptyFormData)));
                  setFormHasChanges(false);
                  setFormStatus({
                    status: null,
                    to_id: null,
                    dept_head_id: null,
                    assignee_role: null,
                  });
                  setRowSelectionModel([]);
                  setInitialRowSelectionModel([]);
                }
              }
            });
          }
        });
      }
      if (activeTab === "Training Needs Forms" && !id) {
        const empIdForDetails =
          urlEmpId && isCompanyPrivileged(data?.role) ? urlEmpId : data.id;
        dispatch(getUserDetails(empIdForDetails)).then(async (res) => {
          if (res.meta.requestStatus === "fulfilled") {
            let designation = "";
            setaTemplate.forEach((item) => {
              if (item?.config?.isDesignation) {
                designation = item?.fieldName;
              }
            });
            const isUnemployedEmp =
              employmentTypes?.length &&
              isUnemployedEmploymentType(
                employmentTypes,
                res.payload?.employment_type,
              );
            let departmentPayload = null;
            if (
              res.payload?.department_id &&
              !isNoDepartment(res.payload.department_id) &&
              !isUnemployedEmp
            ) {
              const department = await dispatch(
                fetchOrganogramByDepartmentId(res.payload.department_id),
              );
              departmentPayload = department?.payload;
            }

            // Fetch department head name
            let departmentHeadName = "-";
            if (departmentPayload?.dept_head_id) {
              try {
                const { data: departmentHeadData, error } =
                  await fromWithCompany("company_user")
                    .select("first_name, last_name, data")
                    .eq("emp_id", departmentPayload.dept_head_id)
                    .neq("status", statuses.ARCHIVED)
                    .single();

                if (!error && departmentHeadData) {
                  departmentHeadName =
                    departmentHeadData.first_name +
                      " " +
                      departmentHeadData.last_name ||
                    departmentHeadData.data?.Name ||
                    departmentHeadData.data?.name ||
                    "No Name";
                }
              } catch (error) {
                console.error("Error fetching manager data:", error);
              }
            }

            setUserDetails({
              ...res.payload,
              designation: res.payload.data?.[designation],
              department: isUnemployedEmp
                ? ""
                : (departmentPayload?.dept_name ??
                  (isNoDepartment(res.payload?.department_id) ? "" : "")),
              department_head_name: departmentHeadName,
            });
          }
        });
      }
    }
    if (activeTab === "Comments") {
      dispatch(fetchComments(selectedFormId));
    }
  }, [activeTab, configData, id, setaTemplate, urlEmpId, employmentTypes]);

  const handleApproveDialog = async (e, status) => {
    e.preventDefault();

    // Get current form data
    const formData = methods.getValues();

    // Validate form data before opening dialog
    try {
      // Mark that validation has been attempted
      setFormValidationAttempted(true);

      // Validate the form using the existing validation schema
      const isValid = await methods.trigger();
      if (!isValid) {
        // toast.error("Please fix the validation errors before proceeding.");
        return;
      }

      // Validate that at least one skill detail is complete
      let required_skills = [];
      formData.skillsDetail.forEach((skill) => {
        if (skill.skillsRequired?.trim() && skill.priorityLevel?.trim()) {
          required_skills.push(skill);
        }
      });

      if (required_skills.length === 0) {
        toast.error("At least one complete skill detail is required.");
        return;
      }

      // If validation passes, open the dialog
      setApprovalDialogState({ id: null, show: true, formStatus: status });
      setCommentError(false); // Clear comment error when dialog opens
      setFormValidationAttempted(false); // Reset validation state
    } catch (error) {
      console.error("Validation error:", error);
      toast.error("Validation failed. Please check the form and try again.");
    }
  };

  // Updated onSubmit function to handle approval validation
  const onSubmit = async (data, saveType) => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    // For approval actions (REQUEST_EDIT, RECOMMENDED, APPROVED), validate comment only
    if (
      saveType === trainingFormStatus.REQUEST_EDIT ||
      saveType === trainingFormStatus.RECOMMENDED ||
      saveType === trainingFormStatus.APPROVED
    ) {
      if (!comment?.trim()) {
        setCommentError(true);
        toast.error("Please provide a comment for this action.");
        setIsSubmitting(false);
        return;
      }
    }

    const localDataForDeadline = getLocalData();
    const isPrivilegedOnBehalfSubmit =
      isCompanyPrivileged(localDataForDeadline?.role) &&
      (formStatus?.status === trainingFormStatus.REQUEST_EDIT ||
        !id ||
        (loadedForm?.company_user?.emp_id &&
          loadedForm.company_user.emp_id !== localDataForDeadline?.emp_id));

    // Double-check submission deadline before allowing submission
    if (
      configData?.employee_submission_date &&
      (saveType === trainingFormStatus.DRAFT ||
        saveType === trainingFormStatus.REQUESTED) &&
      !isPrivilegedOnBehalfSubmit
    ) {
      const submissionDeadlineForSubmit = getEndOfDayFromISOString(
        configData.employee_submission_date,
      );
      const currentDate = moment();
      if (currentDate.isAfter(submissionDeadlineForSubmit)) {
        toast.error(
          `Form submission is closed. The submission deadline was ${moment(
            submissionDeadlineForSubmit,
          ).format("DD/MM/YYYY")}. Please contact your administrator.`,
        );
        setIsSubmitting(false);
        return;
      }
    }

    try {
      if (isUnemployed) {
        const skillsWithContent = data.skillsDetail.filter(
          (s) => s.skillsRequired?.trim() && s.priorityLevel?.trim(),
        );
        if (skillsWithContent.length > 1) {
          toast.error(
            "Unemployed learners are limited to one skill requirement.",
          );
          setIsSubmitting(false);
          return;
        }
        const anyMultiTraining = data.skillsDetail.some(
          (s) =>
            (Array.isArray(s.selectedTrainings)
              ? s.selectedTrainings.length
              : 0) > UNEMPLOYED_MAX_TRAININGS_PER_SKILL,
        );
        if (anyMultiTraining) {
          toast.error(
            `Unemployed learners are limited to ${UNEMPLOYED_MAX_TRAININGS_PER_SKILL} training per skill.`,
          );
          setIsSubmitting(false);
          return;
        }
      }

      let required_skills = [];
      const skillsToProcess = isUnemployed
        ? (data.skillsDetail || []).slice(0, 1)
        : data.skillsDetail || [];

      skillsToProcess.forEach((skill) => {
        if (skill.skillsRequired?.trim() && skill.priorityLevel?.trim()) {
          required_skills.push(skill);
          // if (configData?.training_need_workflow && configData?.select_training) {
          //   required_skills.push({
          //     ...skill,
          //     completionDate: null,
          //   });
          // } else {
          //   required_skills.push(skill);
          // }
        }
      });

      let yearId = await dispatch(fetchYearId());
      const localData = getLocalData();
      const empIdForNewForm =
        !id && isCompanyPrivileged(localData?.role) && urlEmpId
          ? urlEmpId
          : localData?.id;
      const trainingForm = {
        short_term_goal: data.shortTermGoal?.trim(),
        long_term_goal: data.longTermGoal?.trim(),
        notes: data.AnyNotes?.trim(),
        updated_at: currentDate(),
        ...(!id && { emp_id: empIdForNewForm }),
        ...(!id && { year_id: yearId.payload }),
        ...(!id && { company_id: localData?.company_id }),
      };
      const isSdfOnBehalf =
        isCompanyPrivileged(localData?.role) &&
        trainingForm.emp_id &&
        trainingForm.emp_id !== localData?.id;
      if (!(selectedFormId || id)) {
        dispatch(
          addTrainingForm({
            trainingForm,
            required_skills,
            saveType,
            filled_by_sdf_on_behalf: isSdfOnBehalf,
          }),
        )
          .then((res) => {
            if (!res.payload.error) {
              if (saveType === trainingFormStatus.DRAFT) {
                toast.success(messages.DRAFT_SAVED_SUCCESSFULLY);
              } else {
                toast.success(messages.TRAINING_FORM_SUBMITTED_SUCCESSFULLY);
              }
              const { dept_head_id, to_id, form_status, assignee_role } =
                res.payload;
              setFormStatus({
                status: form_status,
                to_id: to_id,
                dept_head_id: dept_head_id,
                assignee_role: assignee_role,
              });

              // Update local state with default training type and category values
              // The trigger creates these in the database, but we update UI immediately
              const updatedRowSelectionModel = rowSelectionModel.map((row) => ({
                ...row,
                selectedTrainingsInfo: row.selectedTrainingsInfo.map(
                  (training) => ({
                    ...training,
                    allocated_training_category:
                      training.allocated_training_category ||
                      DEFAULT_TRAINING_ALLOCATION.TRAINING_CATEGORY,
                    allocated_training_type:
                      training.allocated_training_type ||
                      DEFAULT_TRAINING_ALLOCATION.TRAINING_TYPE,
                  }),
                ),
              }));
              setRowSelectionModel(updatedRowSelectionModel);

              // SDF-on-behalf: redirect to review-training-needs; normal flow: redirect to employee-form-status
              if (isSdfOnBehalf) {
                navigate("/review-training-needs");
              } else {
                navigate("/employee-form-status");
              }
            } else {
              toast.error(messages.SOMETHING_WENT_WRONG);
            }
            setIsSubmitting(false);
          })
          .catch((error) => {
            console.error("Form submission error:", error);
            toast.error(messages.SOMETHING_WENT_WRONG);
            setIsSubmitting(false);
          });
      } else {
        const isSdfOnBehalfUpdate =
          isCompanyPrivileged(getLocalData()?.role) &&
          loadedForm?.company_user?.emp_id &&
          loadedForm.company_user.emp_id !== getLocalData()?.emp_id;
        const effectiveSaveType = saveType
          ? saveType
          : approvalDialogState.formStatus;
        const statusWithHistory = new Set([
          trainingFormStatus.REQUEST_EDIT,
          trainingFormStatus.RECOMMENDED,
          trainingFormStatus.APPROVED,
          trainingFormStatus.REQUESTED,
        ]);
        const useHistoryRpc =
          statusWithHistory.has(effectiveSaveType) ||
          (effectiveSaveType === trainingFormStatus.RECOMMENDED &&
            formStatus?.status === trainingFormStatus.RECOMMENDED);

        // Pure status transitions skip skill rewrite (protects allocations).
        // When skills/trainings changed, include them; RPC remaps allocations.
        const hasSkillOrTrainingChanges = checkFormHasChanges();
        const isStatusOnlyNoSkillResave =
          !hasSkillOrTrainingChanges &&
          (effectiveSaveType === trainingFormStatus.APPROVED ||
            effectiveSaveType === trainingFormStatus.REQUEST_EDIT ||
            (effectiveSaveType === trainingFormStatus.RECOMMENDED &&
              formStatus?.status === trainingFormStatus.RECOMMENDED));

        const shouldIncludeSkillsInSave =
          required_skills.length > 0 &&
          !isStatusOnlyNoSkillResave &&
          (effectiveSaveType === trainingFormStatus.REQUESTED ||
            hasSkillOrTrainingChanges);

        const submitThunk = useHistoryRpc
          ? saveTrainingFormWithHistory({
              form_id: selectedFormId || id,
              target_status:
                effectiveSaveType === trainingFormStatus.RECOMMENDED &&
                formStatus?.status === trainingFormStatus.RECOMMENDED
                  ? null
                  : effectiveSaveType,
              comment,
              training_form: trainingForm,
              required_skills: shouldIncludeSkillsInSave
                ? required_skills
                : null,
              to_id: formStatus?.to_id,
              write_history_on_data_only:
                effectiveSaveType === trainingFormStatus.RECOMMENDED &&
                formStatus?.status === trainingFormStatus.RECOMMENDED,
            })
          : updateTrainingForm({
              trainingForm,
              required_skills,
              form_id: selectedFormId || id,
              to_id: formStatus?.to_id,
              formStatus: approvalDialogState.formStatus,
              saveType: effectiveSaveType,
              filled_by_sdf_on_behalf:
                isSdfOnBehalfUpdate &&
                !(
                  formStatus?.status === trainingFormStatus.REQUEST_EDIT &&
                  effectiveSaveType === trainingFormStatus.REQUESTED
                ),
            });

        dispatch(submitThunk)
          .then((res) => {
            if (res.error) {
              toast.error(res.payload || messages.SOMETHING_WENT_WRONG);
              setIsSubmitting(false);
              return;
            }
            if (!res.payload?.error) {
              if (saveType === trainingFormStatus.DRAFT) {
                toast.success(messages.DRAFT_SAVED_SUCCESSFULLY);
              } else {
                toast.success(messages.TRAINING_FORM_SUBMITTED_SUCCESSFULLY);
              }
              const { dept_head_id, to_id, form_status, assignee_role } =
                res.payload;
              setFormStatus({
                status: form_status || effectiveSaveType,
                to_id: to_id ?? formStatus?.to_id,
                dept_head_id: dept_head_id ?? formStatus?.dept_head_id,
                assignee_role: assignee_role ?? formStatus?.assignee_role,
              });

              const updatedRowSelectionModel = rowSelectionModel.map((row) => ({
                ...row,
                selectedTrainingsInfo: row.selectedTrainingsInfo.map(
                  (training) => ({
                    ...training,
                    allocated_training_category:
                      training.allocated_training_category ||
                      DEFAULT_TRAINING_ALLOCATION.TRAINING_CATEGORY,
                    allocated_training_type:
                      training.allocated_training_type ||
                      DEFAULT_TRAINING_ALLOCATION.TRAINING_TYPE,
                  }),
                ),
              }));
              setRowSelectionModel(updatedRowSelectionModel);

              if (saveType !== trainingFormStatus.DRAFT) {
                setApprovalDialogState({
                  id: null,
                  show: false,
                  formStatus: null,
                });
                if (activeTab === "Comments" && (selectedFormId || id)) {
                  dispatch(fetchComments(selectedFormId || id));
                }
                if (
                  isSdfOnBehalfUpdate &&
                  saveType === trainingFormStatus.REQUESTED
                ) {
                  navigate("/review-training-needs");
                }
              } else if (!isSdfOnBehalfUpdate) {
                navigate("/employee-form-status");
              }
              setComment("");
              setCommentError(false);
              const currentFormDataAfterUpdate = methods.getValues();
              setInitialFormData(
                JSON.parse(JSON.stringify(currentFormDataAfterUpdate)),
              );
              setInitialRowSelectionModel(
                JSON.parse(JSON.stringify(updatedRowSelectionModel)),
              );
              setFormHasChanges(false);
            } else {
              toast.error(messages.SOMETHING_WENT_WRONG);
            }
            setIsSubmitting(false);
          })
          .catch((error) => {
            console.error("Form submission error:", error);
            toast.error(messages.SOMETHING_WENT_WRONG);
            setIsSubmitting(false);
          });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(messages.SOMETHING_WENT_WRONG);
      setIsSubmitting(false);
    }
  };

  const trainingColumns = [
    {
      field: "name",
      headerName: "Training Name",
      minWidth: isWideScreen ? undefined : 150,
      flex: isWideScreen ? 1 : undefined,
    },
    {
      field: "program_duration",
      headerName: "Program Duration",
      minWidth: isWideScreen ? undefined : 150,
      flex: isWideScreen ? 1 : undefined,
      renderCell: (params) => {
        return params.row.program_duration || "-";
      },
    },
    {
      field: "cost",
      headerName: "Cost per Learner",
      minWidth: isWideScreen ? undefined : 150,
      flex: isWideScreen ? 1 : undefined,
    },
    {
      field: "description",
      headerName: "Training Details",
      minWidth: isWideScreen ? undefined : 150,
      flex: isWideScreen ? 1 : undefined,
      renderCell: (params) => {
        const description = params.row.description || "";
        const truncatedDescription =
          description.length > 100
            ? description.substring(0, 100) + "..."
            : description;

        const handleClick = () => {
          if (description) {
            setCourseDetailsDialog({
              open: true,
              description: description,
              title: params.row.name || "Training Details",
            });
          }
        };

        return (
          <span
            className={description ? "cursor-pointer hover:text-blue-500 " : ""}
            onClick={handleClick}
            title="Click to view full description"
          >
            {truncatedDescription}
          </span>
        );
      },
    },
    {
      field: "url",
      headerName: "Training URL",
      minWidth: isWideScreen ? undefined : 150,
      flex: isWideScreen ? 1 : undefined,
      renderCell: (params) => {
        const url = params.row.url || params.row.payload?.url || "";
        if (url) {
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 underline"
              onClick={(e) => e.stopPropagation()}
            >
              Link
            </a>
          );
        }
        return <span>-</span>;
      },
    },
    {
      field: "provider",
      headerName: "Provider",
      minWidth: isWideScreen ? undefined : 150,
      flex: isWideScreen ? 1 : undefined,
    },
    {
      field: "targeted_skills",
      headerName: "Targeted Skills",
      minWidth: isWideScreen ? undefined : 150,
      flex: isWideScreen ? 1 : undefined,
    },
    {
      field: "nqf_aligned",
      headerName: "NQF Aligned",
      minWidth: isWideScreen ? undefined : 150,
      flex: isWideScreen ? 1 : undefined,
      renderCell: (params) => {
        return params.row.nqf_aligned ? "Yes" : "No";
      },
    },
    {
      field: "nqf_level",
      headerName: "NQF Level",
      renderCell: (params) => {
        return params.row.nqf_aligned ? params.row.nqf_level : "-";
      },
    },
  ];

  // const trainingColumns = [
  //   {
  //     field: "Training Name",
  //     headerName: "Training Name",
  //     flex: 1,
  //   },
  //   {
  //     field: "Programme Name",
  //     headerName: "Programme Name",
  //     flex: 1,
  //   },
  //   {
  //     field: "Price",
  //     headerName: "Price",
  //     flex: 1,
  //   },
  //   {
  //     field: "Description",
  //     headerName: "Description",
  //     flex: 1,
  //   },
  //   {
  //     field: "Why this training is required",
  //     headerName: "Why this training is required",
  //     flex: 1,
  //   },
  //   {
  //     field: "Course/Programme type",
  //     headerName: "Course/Programme type",
  //     flex: 1,
  //   },
  //   {
  //     field: "Name of Training Provider",
  //     headerName: "Training Provider",
  //     flex: 1,
  //   },
  //   {
  //     field: "NQF Aligned",
  //     headerName: "NQF Aligned",
  //     flex: 1,
  //   },
  //   {
  //     field: "NQF Level",
  //     headerName: "NQF Level",
  //     flex: 1,
  //   },
  // ];

  const renderHistoryEllipsisCell = useCallback((value) => {
    const text = value || "-";
    return (
      <Tooltip title={text === "-" ? "" : text} arrow placement="top">
        <p className="truncate w-full min-w-0 m-0">{text}</p>
      </Tooltip>
    );
  }, []);

  const formatHistoryActionLabel = useCallback(
    (action) => trainingFormStatusLabels[action] || action || "-",
    [],
  );

  const historyColumnVisibilityModel = useMemo(
    () => (isHistoryCompact ? { role: false } : {}),
    [isHistoryCompact],
  );

  const commentColumns = useMemo(() => {
    const nameWidth = isHistoryCompact ? 108 : isWideScreen ? 148 : 124;
    const roleWidth = isWideScreen ? 158 : 142;
    const actionWidth = isHistoryCompact ? 156 : isWideScreen ? 215 : 188;
    const dateWidth = isHistoryCompact ? 146 : isWideScreen ? 172 : 158;
    const viewWidth = isHistoryCompact ? 64 : 76;
    const commentMinWidth = isHistoryCompact ? 136 : isWideScreen ? 280 : 200;

    return [
      {
        field: "name",
        headerName: "Name",
        width: nameWidth,
        flex: 0,
        renderCell: (params) => renderHistoryEllipsisCell(params.value),
      },
      {
        field: "role",
        headerName: "Role",
        width: roleWidth,
        flex: 0,
        sortable: false,
        renderCell: (params) => renderHistoryEllipsisCell(params.value),
      },
      {
        field: "actionTaken",
        headerName: "Action Taken",
        width: actionWidth,
        flex: 0,
        sortable: false,
        valueGetter: (_value, row) => row.action,
        renderCell: (params) => {
          const label = formatHistoryActionLabel(params.row.action);
          return isWideScreen
            ? <p className="whitespace-nowrap m-0">{label}</p>
            : renderHistoryEllipsisCell(label);
        },
      },
      {
        field: "comment",
        headerName: "Comment",
        flex: 1,
        minWidth: commentMinWidth,
        renderCell: (params) => renderHistoryEllipsisCell(params.row.comment),
      },
      {
        field: "dateTime",
        headerName: "Date & Time",
        width: dateWidth,
        flex: 0,
        sortable: false,
        renderCell: (params) => {
          const label =
            params.row.date && params.row.time
              ? `${params.row.date} ${params.row.time}`
              : "-";
          return isWideScreen
            ? <p className="whitespace-nowrap m-0">{label}</p>
            : renderHistoryEllipsisCell(label);
        },
      },
      {
        field: "view",
        headerName: "View",
        width: viewWidth,
        flex: 0,
        sortable: false,
        renderCell: (params) =>
          params.row.canView ? (
            <CButton
              type="button"
              className="text-blue-500 text-sm underline !p-0 !min-w-0 bg-transparent border-0"
              onClick={() =>
                setHistoryViewDialog({ open: true, row: params.row })
              }
            >
              View
            </CButton>
          ) : (
            "-"
          ),
      },
    ];
  }, [
    formatHistoryActionLabel,
    isHistoryCompact,
    isWideScreen,
    renderHistoryEllipsisCell,
  ]);

  function isEditableStatus(status) {
    const EDITABLE_STATUSES = new Set([
      trainingFormStatus.DRAFT,
      trainingFormStatus.REQUEST_EDIT,
    ]);
    return !status || EDITABLE_STATUSES.has(status);
  }

  const getFormEmployeeEmpId = () =>
    loadedForm?.company_user?.emp_id ?? userDetails?.emp_id ?? null;

  const isOwnEmployeeForm = () => {
    const userData = getLocalData();
    const formEmpId = getFormEmployeeEmpId();
    if (!formEmpId || !userData?.emp_id) return false;
    return String(formEmpId).trim() === String(userData.emp_id).trim();
  };

  const showPrivilegedApprovalActions = () => {
    const userData = getLocalData();
    return isCompanyPrivileged(userData?.role);
  };

  function isDisabled() {
    const userData = getLocalData();
    const userRole = userData?.role;
    const currentUserId = userData?.emp_id;
    const isDeptHead =
      formStatus.dept_head_id != null &&
      String(formStatus.dept_head_id).trim() ===
        String(currentUserId ?? "").trim();

    if (isDeptHead && id) {
      return false;
    }

    if (isCompanyPrivileged(userRole) && showPrivilegedApprovalActions()) {
      return false;
    }

    if (!id) {
      return !isEditableStatus(formStatus.status);
    }

    if (id) {
      if (isOwnEmployeeForm() && isEditableStatus(formStatus.status)) {
        return false;
      }
      return true;
    }

    return true;
  }

  const selectColumns = useMemo(() => {
    /** Fixed min widths (no flex) so many columns keep readable width; grid scrolls horizontally. */
    const w = (n) => ({ minWidth: n });
    const additionalFieldColumns = (setaTemplate || [])
      .filter((field) => field?.config?.isAdditionalField)
      .map((field) => ({
        field: field.fieldName,
        headerName: field.fieldName,
        ...w(150),
        renderCell: (params) => (
          <span className="text-sm">
            {params.row.allocated_additional_field?.[field.fieldName] ??
              params.row[field.fieldName] ??
              "-"}
          </span>
        ),
      }));
    return [
      {
        field: "name",
        headerName: "Training Name",
        ...w(160),
      },
      {
        field: "program_duration",
        headerName: "Program Duration",
        ...w(150),
        renderCell: (params) => {
          return params.row.program_duration || "-";
        },
      },
      {
        field: "allocated_training_category",
        headerName: "Training Category",
        ...w(170),
        renderHeader: () => (
          <div className="flex items-center gap-0.5 !text-xs xl:!text-sm !leading-xs xl:!leading-sm !font-medium">
            <span>Training Category</span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setShowCategoryMatrixDialog(true);
              }}
              className="!p-0 !min-w-0 !ml-0.5"
              sx={{
                color: "#1976d2",
                "&:hover": {
                  color: "#1565c0",
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                },
              }}
              title="View Training Category descriptions"
            >
              <InfoIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </div>
        ),
        renderCell: (params) => (
          <span className="text-sm">
            {params.row.allocated_training_category || "-"}
          </span>
        ),
      },
      {
        field: "allocated_training_type",
        headerName: "Training Type",
        ...w(150),
        renderCell: (params) => (
          <span className="text-sm">
            {params.row.allocated_training_type || "-"}
          </span>
        ),
      },
      ...additionalFieldColumns,
      {
        field: "cost",
        headerName: "Cost per Learner",
        ...w(140),
      },
      {
        field: "description",
        headerName: "Training Details",
        ...w(200),
        renderCell: (params) => {
          const description = params.row.description || "";
          const truncatedDescription =
            description.length > 100
              ? description.substring(0, 100) + "..."
              : description;

          const handleClick = () => {
            if (description) {
              setCourseDetailsDialog({
                open: true,
                description: description,
                title: params.row.name || "Training Details",
              });
            }
          };

          return (
            <span
              className={
                description ? "cursor-pointer hover:text-blue-500 " : ""
              }
              onClick={handleClick}
              title="Click to view full description"
            >
              {truncatedDescription}
            </span>
          );
        },
      },
      {
        field: "url",
        headerName: "Training URL",
        ...w(130),
        renderCell: (params) => {
          const url = params.row.url || "";
          if (url) {
            return (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-700 underline"
                onClick={(e) => e.stopPropagation()}
              >
                Link
              </a>
            );
          }
          return <span>-</span>;
        },
      },
      {
        field: "provider",
        headerName: "Provider",
        ...w(150),
      },
      {
        field: "targeted_skills",
        headerName: "Targeted Skills",
        ...w(160),
      },
      {
        field: "nqf_aligned",
        headerName: "NQF Aligned",
        ...w(130),
        renderCell: (params) => {
          return params.row.nqf_aligned ? "Yes" : "No";
        },
      },
      {
        field: "nqf_level",
        headerName: "NQF Level",
        ...w(120),
      },
      ...(FEATURE_FLAGS.SHOW_COMPLETION_DATE
        ? [
            {
              field: "completionDate",
              headerName: "Completion Date",
              ...w(180),
              renderCell: (params) => (
                <p>{moment(params.row.completionDate).format("DD-MM-YYYY")}</p>
              ),
            },
          ]
        : []),
      !isDisabled() && {
        field: "action",
        headerName: "Action",
        ...w(110),
        renderCell: (params) => (
          <div className="flex 2xl:gap-3 gap-2 items-center h-full">
            {/* Delete Button */}
            <CButton
              tooltip="Delete"
              tooltipPosition={"top"}
              className="!min-w-0 bg-red-100 text-blue-600 hover:bg-red-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
              onClick={(e) => {
                e.preventDefault();
                setDeleteDialogState({ show: true, data: params.row });
              }}
            >
              <DeleteIcon className="!fill-red-500 !text-red-500" />
            </CButton>
          </div>
        ),
      },
    ].filter(Boolean);
  }, [setaTemplate, id, formStatus?.status, formStatus?.dept_head_id]);

  const handleDeleteTraining = (data) => {
    const selectedSkillData = rowSelectionModel.find(
      (row) => row.skill === data.skill,
    );

    if (!selectedSkillData) return courses;

    const updatedSelectedTrainingInfo =
      selectedSkillData.selectedTrainingsInfo.filter(
        (training) => training.id !== data.id,
      );
    const updatedTrainingIds = updatedSelectedTrainingInfo.map((t) => t.id);

    const updatedRowSelectionModel = rowSelectionModel.map((row) => {
      if (row.skill === data.skill) {
        return {
          ...row,
          selectedTrainingIds: updatedTrainingIds,
          selectedTrainingsInfo: updatedSelectedTrainingInfo,
        };
      }
      return row;
    });

    setRowSelectionModel(updatedRowSelectionModel);
    const findIndex = updatedRowSelectionModel.findIndex(
      (row) => row.skill === data.skill,
    );
    methods.setValue(
      `skillsDetail.${findIndex}.selectedTrainings`,
      updatedTrainingIds,
    );
    setDeleteDialogState({ show: false, data: null });
    const hasChanges = checkFormHasChanges();
    setFormHasChanges(hasChanges);
  };

  const handleRowSelection = (newSelection) => {
    const raw = Array.isArray(newSelection)
      ? newSelection
      : [...(newSelection || [])];
    const selection =
      isUnemployed &&
      raw.length > UNEMPLOYED_MAX_TRAININGS_PER_SKILL
        ? raw.slice(-UNEMPLOYED_MAX_TRAININGS_PER_SKILL)
        : raw;

    const updatedRowSelectionModel = [...rowSelectionModel];

    const findIndex = updatedRowSelectionModel.findIndex(
      (row) => row.skill === selectedSkillIndex.skill,
    );

    if (findIndex === -1) {
      if (isUnemployed && updatedRowSelectionModel.length > 0) {
        updatedRowSelectionModel[0] = {
          ...updatedRowSelectionModel[0],
          skill: selectedSkillIndex.skill,
          selectedTrainingIds: selection,
          selectedTrainingsInfo:
            updatedRowSelectionModel[0].selectedTrainingsInfo || [],
        };
      } else {
        updatedRowSelectionModel.push({
          skill: selectedSkillIndex.skill,
          selectedTrainingIds: selection,
          selectedTrainingsInfo: [],
        });
      }
    } else {
      updatedRowSelectionModel[findIndex].selectedTrainingIds = selection;
    }
    setRowSelectionModel(
      isUnemployed
        ? collapseSingleTrainingRowSelectionModel(
            updatedRowSelectionModel,
            UNEMPLOYED_MAX_TRAININGS_PER_SKILL,
          )
        : updatedRowSelectionModel,
    );
  };

  // Add function to get filtered courses (only filters out already selected trainings)
  const getFilteredCourses = () => {
    if (!courses?.length) {
      return [];
    }
    // Collect all selected training IDs across all skills
    const allSelectedTrainingIds = rowSelectionModel.flatMap(
      (row) => row.selectedTrainingsInfo?.map((training) => training.id) || [],
    );

    // Filter out already selected trainings (no client-side search filtering)
    let filteredCourses = courses.filter(
      (course) => !allSelectedTrainingIds.includes(course.id),
    );

    return filteredCourses;
  };

  // Handle search - calls vector database with the search term
  const handleSearch = () => {
    const searchTerm = searchInputValue.trim();
    if (!searchTerm) {
      toast.error("Please enter a search term");
      return;
    }

    // Update searchQuery for reference
    setSearchQuery(searchTerm);

    // Call fetchTrainingProgrammes to search vector database with the new keyword
    dispatch(
      fetchTrainingProgrammes({
        userDesignation: userDetails?.designation,
        requiredSkills: searchTerm,
        company_id: getLocalData().company_id,
      }),
    );
  };

  const handleSelectTraining = (e) => {
    if (
      FEATURE_FLAGS.SHOW_COMPLETION_DATE &&
      (!completionDate || moment(completionDate).isBefore(currentDate()))
    ) {
      toast.error("Completion date must be in the future");
      return;
    }

    // Shallow-copy so setState receives a new array reference; mutating the same
    // reference would skip re-renders and the Selected Trainings table would not update.
    const updated_rowSelectionModel = [...rowSelectionModel];

    let findIndex = rowSelectionModel.findIndex(
      (row) => row.skill === selectedSkillIndex.skill,
    );
    if (findIndex === -1 && isUnemployed && rowSelectionModel.length > 0) {
      findIndex = 0;
    }
    if (!updated_rowSelectionModel[findIndex]?.selectedTrainingIds?.length) {
      toast.error(messages.SELECT_TRAINING_NAME);
      return;
    }

    let selectedIds =
      updated_rowSelectionModel[findIndex].selectedTrainingIds || [];
    if (
      isUnemployed &&
      selectedIds.length > UNEMPLOYED_MAX_TRAININGS_PER_SKILL
    ) {
      selectedIds = selectedIds.slice(-UNEMPLOYED_MAX_TRAININGS_PER_SKILL);
    }
    const findCourses = selectedIds.map((id) => {
      const selectedCourse = courses.find((course) => course.id === id);
      return {
        id: selectedCourse.id,
        name: selectedCourse.name,
        program_duration: selectedCourse.program_duration,
        cost: selectedCourse.cost,
        description: selectedCourse.description,
        targeted_skills: selectedCourse.targeted_skills,
        provider: selectedCourse.provider,
        nqf_aligned: selectedCourse.nqf_aligned,
        nqf_level: selectedCourse.nqf_level,
        url: selectedCourse.url || selectedCourse.payload?.url || "",
        completionDate: completionDate,
        skill: selectedSkillIndex.skill,
      };
    });

    setCompletionDate(null);

    // Replace selectedTrainingsInfo with the newly selected courses to avoid duplicates
    updated_rowSelectionModel[findIndex] = {
      ...updated_rowSelectionModel[findIndex],
      selectedTrainingsInfo: findCourses,
    };

    setRowSelectionModel(updated_rowSelectionModel);
    // set value of selected trainings to skillsDetail based on the skill
    methods.setValue(
      `skillsDetail.${findIndex}.selectedTrainings`,
      updated_rowSelectionModel[findIndex].selectedTrainingsInfo.map(
        (training) => training.id,
      ),
    );
    setDialogState({ id: null, show: false });
    const hasChanges = checkFormHasChanges();
    setFormHasChanges(hasChanges);
  };

  function normalizeSkillField(value) {
    if (value == null) return "";
    return String(value).trim();
  }

  function checkFormHasChanges() {
    const currentValues = methods.getValues();
    if (!currentValues?.skillsDetail || !initialFormData?.skillsDetail)
      return false;
    const currentSkills = currentValues.skillsDetail;
    const initialSkills = initialFormData.skillsDetail;

    const isFilledSkill = (skill) =>
      Boolean(
        normalizeSkillField(skill?.skillsRequired) &&
          normalizeSkillField(skill?.priorityLevel),
      );

    const currentFilled = currentSkills.filter(isFilledSkill);
    const initialFilled = initialSkills.filter(isFilledSkill);
    if (currentFilled.length !== initialFilled.length) return true;

    const maxLen = Math.max(currentSkills.length, initialSkills.length);
    for (let i = 0; i < maxLen; i++) {
      const current = currentSkills[i];
      const initial = initialSkills[i];
      if (!current && !initial) continue;
      if (!current || !initial) {
        if (isFilledSkill(current) || isFilledSkill(initial)) return true;
        continue;
      }

      const skillFields = [
        "skillsRequired",
        "priorityLevel",
        "skillsRequirementMotivation",
        "trainingType",
        "programmeDuration",
        "programDuration",
        "completionDate",
      ];
      if (
        skillFields.some(
          (field) =>
            normalizeSkillField(current[field]) !==
            normalizeSkillField(initial[field]),
        )
      ) {
        return true;
      }

      const currentSelected = Array.isArray(current.selectedTrainings)
        ? current.selectedTrainings.map(String)
        : [];
      const initialSelected = Array.isArray(initial.selectedTrainings)
        ? initial.selectedTrainings.map(String)
        : [];
      if (
        currentSelected.length !== initialSelected.length ||
        currentSelected.some((val, idx) => val !== initialSelected[idx])
      ) {
        return true;
      }
    }
    return false;
  }

  const handleCancelTraining = (e) => {
    setCompletionDate(null);
    setDialogState({ id: null, show: false });
  };

  const handleFetchTrainingProgrammes = (e) => {
    // Get the index from the dropdown value
    const selectedIndex = methods
      .getValues("skillsDetail")
      .findIndex((skill) => skill.skillsRequired === e.target.value);

    // Preserve existing selections by creating a copy of current rowSelectionModel
    const existingSelections = rowSelectionModel;

    setSelectedSkillIndex({ index: selectedIndex, skill: e.target.value });
    dispatch(
      fetchTrainingProgrammes({
        userDesignation: userDetails?.designation,
        requiredSkills: e.target.value,
        company_id: getLocalData().company_id,
      }),
    );
  };

  const handleReset = (e) => {
    e.preventDefault();
    const resetOptions = {
      keepErrors: false,
      keepDirty: false,
      keepIsSubmitted: false,
      keepTouched: false,
      keepIsValid: false,
      keepSubmitCount: false,
    };

    if (initialFormData) {
      methods.reset(JSON.parse(JSON.stringify(initialFormData)), resetOptions);
      setRowSelectionModel(
        JSON.parse(JSON.stringify(initialRowSelectionModel || [])),
      );
    } else {
      methods.reset(
        {
          shortTermGoal: "",
          longTermGoal: "",
          AnyNotes: "",
          skillsDetail: Array(numberOfSkills)
            .fill()
            .map(() => ({
              skillsRequired: "",
              priorityLevel: "",
              programmeDuration: "",
              skillsRequirementMotivation: "",
              ...(configData?.select_training &&
                FEATURE_FLAGS.SHOW_COMPLETION_DATE && {
                  completionDate: null,
                }),
              selectedTrainings: [],
            })),
        },
        resetOptions,
      );
      setRowSelectionModel([]);
    }

    setSelectedSkillIndex({ index: null, skill: null });
    setFormHasChanges(false);
  };

  // const handleComment = async(e) => {
  //   e.preventDefault();
  //   let data = getLocalData();
  //   await onSubmit(methods.getValues(), approvalDialogState.formStatus);
  //   dispatch(
  //     addComment({
  //       formId: id,
  //       comment: comment,
  //       userid: data.id,
  //       formStatus: approvalDialogState.formStatus,
  //       to_id: formStatus.to_id,
  //     })
  //   ).then((res) => {
  //     if (!res.error) {
  //       toast.success('Comment added successfully.');
  //       // const {dept_head_id, to_id, form_status} = res.payload;
  //       // setApprovalDialogState({ id: null, show: false, formStatus: null });
  //       // setFormStatus({
  //       //   status: form_status,
  //       //   to_id: to_id,
  //       //   dept_head_id: dept_head_id,
  //       // });
  //       // setComment("");
  //       // toast.success("Comment added successfully.");
  //     }
  //   });
  // };

  // Helper function to check if training needs collection is accessible
  const isTrainingCollectionAccessible = () => {
    if (isCompanyPrivileged(getLocalData()?.role) && id) {
      return { accessible: true, message: null };
    }

    // Check if training needs workflow is enabled
    if (
      !configData?.training_need_workflow ||
      (configData?.training_need_workflow &&
        !configData?.training_need_collection_start_date)
    ) {
      return {
        accessible: false,
        message: "Training needs collection process has not started yet.",
      };
    }

    if (!configData?.employee_submission_date) {
      return { accessible: true, message: null };
    }

    // Check if current date is before employee submission date
    const currentDate = moment();
    const submissionDeadlineForAccess = getEndOfDayFromISOString(
      configData.employee_submission_date,
    );
    if (
      !submissionDeadlineForAccess ||
      currentDate.isAfter(submissionDeadlineForAccess)
    ) {
      return {
        accessible: false,
        message: `Training needs employee collection process date has passed. The submission deadline was ${
          submissionDeadlineForAccess
            ? moment(submissionDeadlineForAccess).format("DD/MM/YYYY")
            : "Invalid date"
        }.`,
      };
    }

    return { accessible: true, message: null };
  };

  // const collectionStatus = isTrainingCollectionAccessible();
  useEffect(() => {
    if (!configData) return;
    const collectionStatus = isTrainingCollectionAccessible();
    setCollectionStatus(collectionStatus);
  }, [configData, id, formStatus?.status]);
  // Function to check if form has changes
  const checkFormChanges = (currentData) => {
    if (!initialFormData) return false;

    // Deep comparison of form data
    const isEqual =
      JSON.stringify(currentData) === JSON.stringify(initialFormData);
    return !isEqual;
  };

  // Watch for form validation changes and provide real-time feedback
  useEffect(() => {
    const subscription = methods.watch((value, { name, type }) => {
      // When any field changes, trigger validation for that specific field
      if (type === "change" && name) {
        // Trigger validation for the changed field only
        methods.trigger(name);

        // Check if form has changes
        const hasChanges = checkFormChanges(value);
        setFormHasChanges(hasChanges);

        // If validation was previously attempted and form is now valid, show success feedback
        if (formValidationAttempted) {
          methods.trigger().then((isValid) => {
            if (isValid) {
              // Form is now valid, could show a subtle success indicator
              setFormValidationAttempted(false);
            }
          });
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [methods, formValidationAttempted, initialFormData]);
  return (
    <>
      <div>
        <h2 className="!font-[Poppins] xl:text-3xl xl:leading-3xl text-2xl leading-2xl text-blue-500 font-semibold">
          Training Needs Form
        </h2>

        {/* Training Collection Status Notice */}
        {/* {configData?.training_need_collection_start_date && configData?.employee_submission_date && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-blue-600 font-semibold">Submission Deadline:</span>
                <span className="text-gray-700">
                  {moment(configData.employee_submission_date).format('DD/MM/YYYY')}
                </span>
              </div>
              
            </div>
          </div>
        )} */}

        {/* Show form only if collection is accessible */}
        {collectionStatus.accessible ? (
          <>
            <CTabs
              tabs={tabs}
              value={activeTab}
              onChange={setActiveTab}
              className="my-4"
            />
            <CTabPanel value={activeTab} index="Training Needs Forms">
              <div className="border border-blue-200 bg-white rounded-lg lg:p-6 p-4 lg:mb-6 mb-4 relative md:mt-8 mt-4">
                <p className="bg-white text-lg font-semibold text-gray-700 mb-4 2xl:text-xl 2xl:leading-xl lg:text-base lg:leading-base text-sm leading-sm absolute md:top-[-16px] top-[-12px] left-[16px] px-1">
                  Employee Details
                </p>
                <div className="grid lg:grid-cols-5 sm:grid-cols-2 grid-cols-1 sm:gap-2 gap-1">
                  <p className="2xl:text-base text-sm 2xl:leading-base leading-sm text-blue-300 font-medium">
                    Employee ID:{" "}
                    <span className="text-gray-700">
                      {userDetails?.emp_id || "-"}
                    </span>
                  </p>
                  <p className="2xl:text-base text-sm 2xl:leading-base leading-sm text-blue-300 font-medium">
                    Employee Name:{" "}
                    <span className="text-gray-700">
                      {userDetails?.first_name + " " + userDetails?.last_name ||
                        userDetails?.data?.Name ||
                        "-"}
                    </span>
                  </p>
                  <p className="2xl:text-base text-sm 2xl:leading-base leading-sm text-blue-300 font-medium">
                    Designation:{" "}
                    <span className="text-gray-700">
                      {userDetails?.designation || "-"}
                    </span>
                  </p>
                  <p className="2xl:text-base text-sm 2xl:leading-base leading-sm text-blue-300 font-medium ">
                    Department:{" "}
                    <span className="text-gray-700">
                      {userDetails?.department || "-"}
                    </span>
                  </p>
                  <p className="2xl:text-base text-sm 2xl:leading-base leading-sm text-blue-300 font-medium">
                    Manager:{" "}
                    <span className="text-gray-700">
                      {userDetails?.department_head_name || "-"}
                    </span>
                  </p>
                </div>
              </div>
              <div className="border border-blue-200 bg-white rounded-lg md:p-6 p-4 md:mb-6 mb-4 relative md:mt-8 mt-6">
                <p className="bg-white text-lg font-semibold text-gray-700 mb-4 2xl:text-xl 2xl:leading-xl lg:text-base lg:leading-base text-sm leading-sm absolute md:top-[-16px] top-[-12px] left-[16px] px-1">
                  Training Needs Details
                </p>

                {/* Select Trainings Dialog */}
                {dialogState.show && (
                  <CDialog
                    open={dialogState.show}
                    onClose={() => {
                      setDialogState({ id: null, show: false });
                      setSearchQuery("");
                      setSearchInputValue("");
                      setCourseDetailsDialog({
                        open: false,
                        description: "",
                        title: "",
                      });
                    }}
                    className="trainingFormDialog bg-[#95AEBB80] !p-0"
                  >
                    <div className="relative">
                      <div className="flex justify-between md:py-5 py-3 md:px-6 px-4 border-b border-blue-200 sticky top-0 bg-white z-10">
                        <p className="2xl:text-xl 2xl:leading-xl text-base leading-base font-semibold text-blue-500">
                          Select Training for{" "}
                          {searchQuery || selectedSkillIndex.skill || "Skill"}
                          {isUnemployed && (
                            <span className="text-sm font-normal text-gray-600 block mt-1">
                              Unemployed learners: only{" "}
                              {UNEMPLOYED_MAX_TRAININGS_PER_SKILL} training can
                              be selected.
                            </span>
                          )}
                        </p>
                        <CButton
                          className="!h-auto !p-0 text-blue-500 font-semibold !ml-auto !underline"
                          onClick={() =>
                            setDialogState({ id: null, show: false })
                          }
                        >
                          <GridCloseIcon className="text-gray-700" />
                        </CButton>
                      </div>
                      <div className="p-4 h-[calc(100vh-150px)] overflow-y-auto relative">
                        <div className="flex justify-between items-end mb-2 md:flex-nowrap flex-wrap">
                          <div className="flex gap-2 md:flex-nowrap md:w-auto w-full flex-1">
                            <div className="relative flex-1 md:max-w-[400px]">
                              <CTextField
                                placeholder="Search trainings..."
                                value={searchInputValue}
                                onChange={(e) =>
                                  setSearchInputValue(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleSearch();
                                  }
                                }}
                                className="w-full"
                                InputProps={{
                                  startAdornment: (
                                    <SearchIcon className="text-gray-400 mr-2" />
                                  ),
                                }}
                              />
                            </div>
                            {FEATURE_FLAGS.SHOW_COMPLETION_DATE && (
                              <CDatePicker
                                label="Completion Date"
                                className="filter-date-picker lg:!min-w-[200px] md:w-auto !min-w-[calc(50%-4px)]"
                                value={completionDate}
                                minDate={moment().toDate()}
                                onChange={(e) => {
                                  if (moment(e).isBefore(currentDate())) {
                                    toast.error(
                                      "Completion date must be in the future",
                                    );
                                    return;
                                  }
                                  setCompletionDate(e);
                                  if (selectedSkillIndex.index !== null) {
                                    methods.setValue(
                                      `skillsDetail.${selectedSkillIndex.index}.completionDate`,
                                      getDate(e),
                                    );
                                  }
                                }}
                              />
                            )}
                          </div>
                          <div className="flex gap-2 md:flex-nowrap flex-wrap mt-2 md:w-auto w-full">
                            <CButton
                              className="!p-0 text-white bg-blue-500 hover:bg-blue-700 border border-blue-500 hover:border-blue-700  !ml-auto !px-6 md:!w-auto !w-[calc(50%-4px)]"
                              onClick={(e) => handleSelectTraining(e)}
                            >
                              Select
                            </CButton>
                            <CButton
                              className="!p-0 text-blue-500  hover:bg-blue-500 hover:text-white border border-blue-500 hover:border-blue-500  !ml-auto !px-6 md:!w-auto !w-[calc(50%-4px)]"
                              onClick={handleCancelTraining}
                            >
                              Cancel
                            </CButton>
                          </div>
                        </div>
                        <div className="border-r border-l border-b border-blue-200 rounded-lg overflow-hidden">
                          <CDataGrid
                            idKey="id"
                            rows={getFilteredCourses()}
                            columns={trainingColumns}
                            checkboxSelection
                            disableMultipleRowSelection={
                              isUnemployed
                            }
                            onRowSelectionModelChange={(newSelection) =>
                              handleRowSelection(newSelection)
                            }
                            rowSelectionModel={
                              rowSelectionModel.find(
                                (row) => row.skill === selectedSkillIndex.skill,
                              )?.selectedTrainingIds || []
                            }
                            initialState={{
                              rowSelection: {
                                selectedRows:
                                  rowSelectionModel.find(
                                    (row) =>
                                      row.skill === selectedSkillIndex.skill,
                                  )?.selectedTrainingIds || [],
                              },
                            }}
                          />
                        </div>
                      </div>

                      {/* Training Details Popup within Select Training Dialog */}
                      {courseDetailsDialog.open && (
                        <div className="absolute inset-0 bg-white z-50 flex flex-col max-h-[90vh]">
                          {/* Header - Centered */}
                          <div className="flex items-center justify-center p-6 pb-4">
                            <h2 className="text-xl font-semibold text-gray-800">
                              Training Details
                            </h2>
                          </div>

                          {/* Content - Description shown directly */}
                          <div className="flex-1 overflow-y-auto px-6 pb-6">
                            <div className="text-base text-gray-800 leading-relaxed whitespace-pre-wrap">
                              {courseDetailsDialog.description ||
                                "No description available."}
                            </div>
                          </div>

                          {/* Footer - Only Close button */}
                          <div className="flex justify-center p-6 pt-4">
                            <CButton
                              className="bg-blue-500 hover:bg-blue-700 min-w-[100px] text-white border border-solid border-blue-500 hover:border-blue-700"
                              onClick={() =>
                                setCourseDetailsDialog({
                                  open: false,
                                  description: "",
                                  title: "",
                                })
                              }
                            >
                              Close
                            </CButton>
                          </div>
                        </div>
                      )}
                    </div>
                  </CDialog>
                )}
                <CFormProvider
                  methods={methods}
                  onSubmit={methods.handleSubmit(onSubmit)}
                  className="custom-input-group"
                >
                  <div className="border border-blue-200 bg-white rounded-lg relative md:overflow-hidden overflow-x-auto md:my-4 my-2">
                    <table className="md:w-full w-[800px] employee_form_table">
                      <thead>
                        <tr>
                          <th className="text-left bg-blue-100 2xl:p-3 p-2 font-semibold text-sm leading-sm">
                            <div className="flex items-center gap-1">
                              Skills Required
                              <Tooltip
                                title="Enter the skill that you need training for"
                                arrow
                                placement="top"
                              >
                                <InfoIcon className="w-4 h-4 text-blue-500 cursor-pointer" />
                              </Tooltip>
                            </div>
                          </th>
                          <th className="text-left bg-blue-100 2xl:p-3 p-2 font-semibold text-sm leading-sm">
                            <div className="flex items-center gap-1">
                              Course Type
                              <Tooltip
                                title="Select the course type (e.g. Qualification or Short Programme)"
                                arrow
                                placement="top"
                              >
                                <InfoIcon className="w-4 h-4 text-blue-500 cursor-pointer" />
                              </Tooltip>
                            </div>
                          </th>
                          <th className="text-left bg-blue-100 2xl:p-3 p-2 font-semibold text-sm leading-sm">
                            <div className="flex items-center gap-1">
                              Priority Level
                              <Tooltip
                                title="Select the priority level for this training requirement"
                                arrow
                                placement="top"
                              >
                                <InfoIcon className="w-4 h-4 text-blue-500 cursor-pointer" />
                              </Tooltip>
                            </div>
                          </th>
                          <th className="text-left bg-blue-100 2xl:p-3 p-2 font-semibold text-sm leading-sm">
                            <div className="flex items-center gap-1">
                              Skills Requirement Motivation
                              <Tooltip
                                title="Explain why this training is needed (minimum 20 characters)"
                                arrow
                                placement="top"
                              >
                                <InfoIcon className="w-4 h-4 text-blue-500 cursor-pointer" />
                              </Tooltip>
                            </div>
                          </th>
                          <th className="text-left bg-blue-100 2xl:p-3 p-2 font-semibold text-sm leading-sm"></th>
                          {!configData?.select_training &&
                            FEATURE_FLAGS.SHOW_COMPLETION_DATE && (
                              <th className="text-left bg-blue-100 2xl:p-3 p-2 font-semibold text-sm leading-sm max-w-[150px]">
                                Completion Date
                              </th>
                            )}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Unemployed: show only 1 training need row; employed: show all 3 */}
                        {(isUnemployed ? fields.slice(0, 1) : fields).map(
                          (field, index) => (
                            <tr key={field.id}>
                              <td className="text-left 2xl:p-3 p-2 text-sm leading-sm align-sub">
                                <CFormTextField
                                  name={`skillsDetail.${index}.skillsRequired`}
                                  className="table-inputs"
                                  error={Boolean(
                                    methods.formState.errors?.skillsDetail?.[
                                      index
                                    ]?.skillsRequired,
                                  )}
                                  helperText={
                                    methods.formState.errors?.skillsDetail?.[
                                      index
                                    ]?.skillsRequired?.message
                                  }
                                  disabled={isDisabled()}
                                />
                              </td>
                              <td className="text-left 2xl:p-3 p-2 text-sm leading-sm align-sub">
                                <CFormSelect
                                  name={`skillsDetail.${index}.programmeDuration`}
                                  className="table-inputs"
                                  options={PROGRAM_DURATION_OPTIONS}
                                  placeholder="Select"
                                  error={Boolean(
                                    methods.formState.errors?.skillsDetail?.[
                                      index
                                    ]?.programmeDuration,
                                  )}
                                  helperText={
                                    methods.formState.errors?.skillsDetail?.[
                                      index
                                    ]?.programmeDuration?.message
                                  }
                                  disabled={isDisabled()}
                                />
                              </td>
                              <td className="text-left 2xl:p-3 p-2 text-sm leading-sm align-sub">
                                <CFormSelect
                                  name={`skillsDetail.${index}.priorityLevel`}
                                  className="table-inputs"
                                  options={priorityOptions}
                                  placeholder="Select"
                                  error={Boolean(
                                    methods.formState.errors?.skillsDetail?.[
                                      index
                                    ]?.priorityLevel,
                                  )}
                                  helperText={
                                    methods.formState.errors?.skillsDetail?.[
                                      index
                                    ]?.priorityLevel?.message
                                  }
                                  disabled={isDisabled()}
                                />
                              </td>
                              <td className="text-left 2xl:p-3 p-2 text-sm leading-sm align-sub">
                                <CFormTextField
                                  name={`skillsDetail.${index}.skillsRequirementMotivation`}
                                  className="table-inputs"
                                  disabled={isDisabled()}
                                  error={Boolean(
                                    methods.formState.errors?.skillsDetail?.[
                                      index
                                    ]?.skillsRequirementMotivation,
                                  )}
                                  helperText={
                                    methods.formState.errors?.skillsDetail?.[
                                      index
                                    ]?.skillsRequirementMotivation?.message
                                  }
                                />
                              </td>
                              <td className="text-left 2xl:p-3 p-2 text-sm leading-sm align-sub">
                                <CButton
                                  className="!h-auto !p-2 text-blue-500 font-semibold !underline"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    const skillValue = methods.getValues(
                                      `skillsDetail.${index}.skillsRequired`,
                                    );

                                    if (!skillValue || !skillValue.trim()) {
                                      toast.error(
                                        "Please enter a skill requirement first",
                                      );
                                      return;
                                    }

                                    setSelectedSkillIndex({
                                      index: index,
                                      skill: skillValue,
                                    });
                                    dispatch(
                                      fetchTrainingProgrammes({
                                        userDesignation:
                                          userDetails?.designation,
                                        requiredSkills: skillValue,
                                        company_id: getLocalData().company_id,
                                      }),
                                    );

                                    // Set default search value to skill name and search immediately
                                    setSearchInputValue(skillValue);
                                    setSearchQuery(skillValue);
                                    setDialogState({
                                      id: null,
                                      show: true,
                                      type: "Select Trainings",
                                    });
                                  }}
                                  type="button"
                                  disabled={isDisabled()}
                                >
                                  Select Training
                                </CButton>
                              </td>
                              {!configData?.select_training &&
                                FEATURE_FLAGS.SHOW_COMPLETION_DATE && (
                                  <td className="text-left 2xl:p-3 p-2 text-sm leading-sm  align-sub max-w-[150px]">
                                    <CFormDatePicker
                                      name={`skillsDetail.${index}.completionDate`}
                                      className="table-inputs"
                                      minDate={moment().toDate()}
                                      error={Boolean(
                                        methods.formState.errors
                                          ?.skillsDetail?.[index]
                                          ?.completionDate,
                                      )}
                                      helperText={
                                        methods.formState.errors
                                          ?.skillsDetail?.[index]
                                          ?.completionDate?.message
                                      }
                                      disabled={isDisabled()}
                                      readOnly={true}
                                    />
                                  </td>
                                )}
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>

                  {configData?.select_training && (
                    <>
                      {!isDisabled() && (
                        <div className="flex justify-between  md:flex-nowrap flex-wrap flex-col-reverse gap-3">
                          Selected Trainings{" "}
                          <div className="flex justify-end gap-3">
                            {configData?.add_adhoc_training &&
                              !isUnemployed && (
                                <CButton
                                  className="!h-auto !p-0 text-blue-500 font-semibold !underline"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleRequestTrainingOpen();
                                  }}
                                  type="button"
                                >
                                  Request Training
                                </CButton>
                              )}
                          </div>
                        </div>
                      )}
                      <div className="border border-blue-200 bg-white rounded-lg relative w-full max-w-full overflow-x-auto md:my-4 my-3 employee_form_traning_table">
                        <CDataGrid
                          rows={selectedTrainingsTableRows}
                          columns={selectColumns}
                          idKey="id"
                          pageSize={5}
                          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        />
                      </div>

                      <TrainingTypeCategoryMatrixDialog
                        open={showCategoryMatrixDialog}
                        onClose={() => setShowCategoryMatrixDialog(false)}
                      />
                      <CDialog
                        open={deleteDialogState.show}
                        onClose={() =>
                          setDeleteDialogState({ show: false, data: null })
                        }
                      >
                        <div className="p-6">
                          <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Delete Training
                          </h3>
                          <p className="text-gray-600 mb-6">
                            Are you sure you want to delete this training?
                          </p>
                          <div className="flex justify-end gap-3">
                            <CButton
                              onClick={() =>
                                setDeleteDialogState({
                                  show: false,
                                  data: null,
                                })
                              }
                              className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded-lg"
                            >
                              Cancel
                            </CButton>
                            <CButton
                              onClick={() => {
                                handleDeleteTraining(deleteDialogState.data);
                              }}
                              className="bg-red-500 text-white hover:bg-red-600 px-4 py-2 rounded-lg"
                            >
                              Delete
                            </CButton>
                          </div>
                        </div>
                      </CDialog>
                    </>
                  )}
                  <div className="mt-6">
                    <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                      <CFormTextField
                        name="shortTermGoal"
                        label="Short Term Goal"
                        placeholder="Short Term Goal"
                        rows={3}
                        multiline
                        className="training-textarea col-span-2"
                        disabled={isDisabled()}
                      />
                      <CFormTextField
                        name="longTermGoal"
                        label="Long Term Goal"
                        placeholder="Long Term Goal"
                        rows={3}
                        multiline
                        className="training-textarea col-span-2"
                        disabled={isDisabled()}
                      />
                      <CFormTextField
                        name="AnyNotes"
                        label="Any Notes"
                        placeholder="Any Notes"
                        rows={3}
                        multiline
                        className="training-textarea col-span-2"
                        disabled={isDisabled()}
                      />
                    </div>
                    {/* Show buttons for draft status or when user is department head */}
                    {!isDisabled() && (
                      <div className="mt-4 flex md:flex-nowrap flex-wrap md:justify-end justify-center gap-2">
                        {/* Draft/Reset buttons for new forms or forms in draft status */}
                        {(!id ||
                          formStatus.status === trainingFormStatus.DRAFT ||
                          (formStatus.status ===
                            trainingFormStatus.REQUEST_EDIT &&
                            (String(formStatus.dept_head_id ?? "").trim() ===
                              String(getLocalData()?.emp_id ?? "").trim() ||
                              isCompanyPrivileged(
                                getLocalData()?.role,
                              )))) && (
                          <>
                            <CButton
                              type="button"
                              className="md:w-auto w-[calc(50%-4px)] transparent text-blue-500 hover:bg-blue-500 hover:text-white border border-blue-500"
                              onClick={(e) => handleReset(e)}
                            >
                              Reset
                            </CButton>
                            {!isUnemployed && (
                              <CButton
                                type="button"
                                className="md:w-auto w-[calc(50%-4px)] transparent text-blue-500 hover:bg-blue-500 hover:text-white border border-blue-500"
                                disabled={isSubmitting}
                                onClick={(e) => {
                                  e.preventDefault();
                                  const rawData = methods.getValues();
                                  onSubmit(rawData, trainingFormStatus.DRAFT);
                                }}
                              >
                                {isSubmitting ? "Saving..." : "Save as Draft"}
                              </CButton>
                            )}
                            {/* Submit button for all forms */}
                            <CButton
                              type="button"
                              className="md:w-auto w-full bg-blue-500 text-white hover:bg-blue-700 border border-blue-500 hover:border-blue-700"
                              disabled={isSubmitting}
                              onClick={(e) => {
                                e.preventDefault();
                                methods.handleSubmit((data) =>
                                  onSubmit(data, trainingFormStatus.REQUESTED),
                                )();
                              }}
                            >
                              {isSubmitting
                                ? "Submitting..."
                                : "Submit Request"}
                            </CButton>
                          </>
                        )}

                        {/* Approval/Edit buttons for existing forms (not in draft status) */}
                        {id &&
                          ((formStatus.status !== trainingFormStatus.DRAFT &&
                            formStatus.status !==
                              trainingFormStatus.REQUEST_EDIT) ||
                            (showPrivilegedApprovalActions() &&
                              formStatus.status !==
                                trainingFormStatus.REQUEST_EDIT &&
                              formStatus.status !==
                                trainingFormStatus.DRAFT)) && (
                            <>
                              {!isUnemployed && (
                                <CButton
                                  type="button"
                                  className="transparent text-blue-500 hover:bg-blue-500 hover:text-white border border-blue-500"
                                  onClick={(e) => {
                                    handleApproveDialog(
                                      e,
                                      trainingFormStatus.REQUEST_EDIT,
                                    );
                                  }}
                                >
                                  Request Edit
                                </CButton>
                              )}

                              <div className="flex items-center gap-2">
                                {showPrivilegedApprovalActions() ? (
                                  <>
                                    {formStatus.status !==
                                      trainingFormStatus.APPROVED && (
                                      <CButton
                                        type="button"
                                        className={`${
                                          formStatus.status ==
                                            trainingFormStatus.RECOMMENDED &&
                                          !formHasChanges
                                            ? "bg-gray-300 text-gray-500 border border-gray-300 cursor-not-allowed"
                                            : "bg-blue-500 text-white hover:bg-blue-700 border border-blue-500 hover:border-blue-700"
                                        }`}
                                        onClick={(e) => {
                                          if (
                                            formStatus.status !==
                                              trainingFormStatus.RECOMMENDED ||
                                            formHasChanges
                                          ) {
                                            handleApproveDialog(
                                              e,
                                              trainingFormStatus.RECOMMENDED,
                                            );
                                          }
                                        }}
                                        disabled={
                                          formStatus.status ==
                                            trainingFormStatus.RECOMMENDED &&
                                          !formHasChanges
                                        }
                                      >
                                        {formStatus.status ==
                                        trainingFormStatus.RECOMMENDED
                                          ? "Save"
                                          : "Recommend"}
                                      </CButton>
                                    )}
                                    {formStatus.status ===
                                      trainingFormStatus.RECOMMENDED && (
                                      <CButton
                                        type="button"
                                        className="bg-blue-500 text-white hover:bg-blue-700 border border-blue-500 hover:border-blue-700"
                                        onClick={(e) =>
                                          handleApproveDialog(
                                            e,
                                            trainingFormStatus.APPROVED,
                                          )
                                        }
                                      >
                                        Approve
                                      </CButton>
                                    )}
                                    {formStatus.status ===
                                      trainingFormStatus.APPROVED && (
                                      <CButton
                                        type="button"
                                        className="transparent text-blue-500 hover:bg-blue-500 hover:text-white border border-blue-500"
                                        onClick={(e) =>
                                          handleApproveDialog(
                                            e,
                                            trainingFormStatus.RECOMMENDED,
                                          )
                                        }
                                      >
                                        Return to Recommended
                                      </CButton>
                                    )}
                                  </>
                                ) : (
                                  <CButton
                                    type="button"
                                    className="bg-blue-500 text-white hover:bg-blue-700 border border-blue-500 hover:border-blue-700"
                                    onClick={(e) =>
                                      handleApproveDialog(
                                        e,
                                        trainingFormStatus.RECOMMENDED,
                                      )
                                    }
                                  >
                                    Recommend
                                  </CButton>
                                )}
                              </div>
                            </>
                          )}
                      </div>
                    )}
                  </div>
                  {/* Approval Dialog */}
                  <CommentDialog
                    open={approvalDialogState.show}
                    onClose={() =>
                      setApprovalDialogState({
                        id: null,
                        show: false,
                        formStatus: null,
                      })
                    }
                    onSubmit={() =>
                      onSubmit(
                        methods.getValues(),
                        approvalDialogState.formStatus,
                      )
                    }
                    comment={comment}
                    onCommentChange={(e) => setComment(e.target.value)}
                    loading={isSubmitting}
                  />
                </CFormProvider>

                {/* Request Training Dialog */}
                <CDialog
                  open={requestTrainingDialogOpen}
                  onClose={handleRequestTrainingCancel}
                  maxWidth={false}
                >
                  <div className="md:w-[700px] w-full md:p-6 p-4 overflow-x-hidden rounded-lg">
                    <div className="flex justify-between items-center mb-6">
                      <p className="2xl:text-xl text-base font-semibold text-blue-500">
                        Request Training
                      </p>
                      <CButton
                        className="!h-auto !p-0 !min-w-0"
                        onClick={handleRequestTrainingCancel}
                      >
                        <GridCloseIcon className="text-gray-700 hover:text-gray-900 cursor-pointer" />
                      </CButton>
                    </div>
                    <CFormProvider
                      key="request-training-form"
                      methods={requestTrainingForm}
                      onSubmit={requestTrainingForm.handleSubmit(
                        handleRequestTrainingAdd,
                      )}
                    >
                      <div className="flex flex-col gap-4 mb-6">
                        <CFormTextField
                          name="trainingName"
                          label="Training Required"
                          placeholder="Lorem ipsum dolores sit"
                          error={Boolean(
                            requestTrainingForm.formState.errors?.trainingName,
                          )}
                          helperText={
                            requestTrainingForm.formState.errors?.trainingName
                              ?.message
                          }
                          className="2xl:!mt-0 !mt-0"
                        />
                        <CFormTextField
                          name="targetedSkills"
                          label="Motivation"
                          multiline
                          rows={3}
                          placeholder="Lorem ipsum dolores sit"
                          error={Boolean(
                            requestTrainingForm.formState.errors
                              ?.targetedSkills,
                          )}
                          helperText={
                            requestTrainingForm.formState.errors?.targetedSkills
                              ?.message
                          }
                          className="2xl:!mt-0 !mt-0"
                        />
                        <CFormSelect
                          name="programDuration"
                          label="Programme Duration"
                          options={PROGRAM_DURATION_OPTIONS}
                          placeholder="Select User"
                          error={Boolean(
                            requestTrainingForm.formState.errors
                              ?.programDuration,
                          )}
                          helperText={
                            requestTrainingForm.formState.errors
                              ?.programDuration?.message
                          }
                          className="2xl:!mt-0 !mt-0"
                        />
                      </div>
                      <div className="flex justify-start gap-3 mt-6">
                        <CButton
                          type="submit"
                          disabled={isSubmitting}
                          className="bg-blue-500 text-white hover:bg-blue-700 border border-blue-500 hover:border-blue-700 min-w-[80px] rounded-lg px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? "Adding..." : "Add"}
                        </CButton>
                        <CButton
                          type="button"
                          className="bg-white hover:bg-gray-50 border border-solid border-blue-500 hover:border-blue-500 min-w-[80px] text-blue-500 hover:text-blue-700 rounded-lg px-4 py-2"
                          onClick={handleRequestTrainingCancel}
                        >
                          Cancel
                        </CButton>
                      </div>
                    </CFormProvider>
                  </div>
                </CDialog>

                {/* Training Details Dialog */}
                <CDialog
                  open={courseDetailsDialog.open}
                  onClose={() =>
                    setCourseDetailsDialog({
                      open: false,
                      description: "",
                      title: "",
                    })
                  }
                  maxWidth="lg"
                >
                  <div className="md:w-[900px] w-full flex flex-col max-h-[90vh]">
                    {/* Header - Centered */}
                    <div className="flex items-center justify-center p-6 pb-4">
                      <h2 className="text-xl font-semibold text-gray-800">
                        Training Details
                      </h2>
                    </div>

                    {/* Content - Description shown directly */}
                    <div className="flex-1 overflow-y-auto px-6 pb-6">
                      <div className="text-base text-gray-800 leading-relaxed whitespace-pre-wrap">
                        {courseDetailsDialog.description ||
                          "No description available."}
                      </div>
                    </div>

                    {/* Footer - Only Close button */}
                    <div className="flex justify-center p-6 pt-4">
                      <CButton
                        className="bg-blue-500 hover:bg-blue-700 min-w-[100px] text-white border border-solid border-blue-500 hover:border-blue-700"
                        onClick={() =>
                          setCourseDetailsDialog({
                            open: false,
                            description: "",
                            title: "",
                          })
                        }
                      >
                        Close
                      </CButton>
                    </div>
                  </div>
                </CDialog>
              </div>
            </CTabPanel>
            <CTabPanel value={activeTab} index="Comments">
              <FormHistoryCompareDialog
                open={historyViewDialog.open}
                onClose={() =>
                  setHistoryViewDialog({ open: false, row: null })
                }
                historyRow={historyViewDialog.row}
                setaTemplate={setaTemplate}
              />
              <div className="border border-blue-200 bg-white rounded-lg  md:mb-6 mb-4 relative md:mt-8 mt-4 overflow-hidden">
                <div className="flex justify-between items-center p-3">
                  <CTextField
                    name="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    startIcon={<SearchIcon className="text-blue-300" />}
                    placeholder={"Search"}
                    className="!mt-0 w-full 2xl:max-w-[400px] max-w-[300px]"
                  />
                </div>
                <div className="overflow-x-auto">
                  <CDataGrid
                    idKey={"id"}
                    searchQuery={searchQuery}
                    rows={comments}
                    columns={commentColumns}
                    columnVisibilityModel={historyColumnVisibilityModel}
                    pinActionsColumn={false}
                    sx={{
                      minWidth: isHistoryCompact ? 560 : isWideScreen ? 0 : 720,
                      "& .MuiDataGrid-cell": {
                        overflow: "hidden",
                      },
                      "& .MuiDataGrid-virtualScrollerContent": {
                        minWidth: "100%",
                      },
                    }}
                  />
                </div>
              </div>
            </CTabPanel>
          </>
        ) : (
          <div className="flex items-center justify-center min-h-[calc(100vh-300px)]">
            <div className="text-center text-gray-500">
              <p className="text-lg">{collectionStatus.message}.</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EmployeeForm;
